'use client'

import { useMutation, useQuery } from '@apollo/client'
import { Badge, Button, Card, Label } from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { useWorkspaceSetup } from '../../contexts/setup-workspace-context'
import { INITIATE_WORKSPACE_VERIFICATION } from '../../data/mutations/workspace'
import { GET_COMPANY_VERIFICATION_METHODS } from '../../data/queries/workspace'
import { useSetupWorkspaceModal } from '../../stores/setup-workspace-modal-store'
import { SetupWorkspaceLayout } from './setup-workspace-layout'

/**
 * This component is used to confirm the company and initiate workspace verification.
 */
export const CompanyConfirmation = () => {
  const { companyDetails, companyIdentifierType, companyIdentifierValue, setAllowableVerificationMethods, setStep } =
    useWorkspaceSetup()

  const { workspaceID } = useSetupWorkspaceModal()
  const [confirmingCompany, setConfirmingCompany] = useState<boolean>(false)

  const { refetch: getCompanyVerificationMethods } = useQuery(GET_COMPANY_VERIFICATION_METHODS, {
    skip: true
  })

  const [initiateWorkspaceVerification] = useMutation(INITIATE_WORKSPACE_VERIFICATION, {
    onCompleted: async (res) => {
      if (res.initiateWorkspaceVerification?.isVerified) {
        setConfirmingCompany(false)
        /**
         * If the user is automatically validated, they will go to the final step.
         */
        setStep(10) // Setting the step to 10 will trigger the next step in the setup process which is the ConfirmationStep component.
      } else {
        /**
         * If the user is not auto-verified, we get the company verification methods list and move to the next step
         */
        try {
          const result = await getCompanyVerificationMethods({
            companyIdentifier: {
              type: companyIdentifierType,
              value: companyIdentifierValue
            },
            workspaceID: workspaceID
          })

          if (result.data?.companyVerificationMethods) {
            setAllowableVerificationMethods(result.data.companyVerificationMethods)
            setStep(5) // Setting the step to 5 will trigger the next step in the setup process which is the SelectVerificationMethod component.
          } else {
            toast.error('No verification methods found')
          }
          setConfirmingCompany(false)
        } catch (error) {
          toast.error('No verification methods found')
          setConfirmingCompany(false)
        }
      }
    },
    onError: (error) => {
      toast.error(error.message)
      setConfirmingCompany(false)
    }
  })

  const onSubmit = () => {
    const input = {
      companyIdentifier: {
        type: companyIdentifierType,
        value: companyIdentifierValue
      },
      workspaceID: workspaceID
    }

    setConfirmingCompany(true)
    initiateWorkspaceVerification({
      variables: input
    })
  }

  return (
    <SetupWorkspaceLayout
      title="We found a company that matches!"
      footer={{
        label: 'No, go back',
        onClick: () => setStep(3) // Setting the step to 3 will trigger the previous step in the setup process which is the MCOrDOTNumber component.
      }}
    >
      <Label className="font-light">Is this your company?</Label>
      <Card className="p-4 mt-2">
        <div className="flex items-center gap-2">
          {companyDetails?.allowedToOperate ? (
            <Badge variant="secondary" className="bg-green-300 text-green-800">
              Active
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-red-300 text-red-800">
              Inactive
            </Badge>
          )}
          {!!companyDetails?.authorizedForProperty && (
            <Badge variant="secondary" className="bg-teal-300 text-teal-800">
              Property
            </Badge>
          )}
          {!!companyDetails?.authorizedForBroker && companyDetails.brokerAuthorityStatus === 'Active' && (
            <Badge variant="secondary" className="bg-purple-300 text-purple-800">
              Broker
            </Badge>
          )}
          {!!companyDetails?.allowedToOperate &&
            (companyDetails.commonAuthorityStatus === 'Active' ||
              companyDetails.contractAuthorityStatus === 'Active') && (
              <Badge variant="secondary" className="bg-blue-300 text-blue-800">
                Carrier
              </Badge>
            )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Label className="text-base">{companyDetails?.companyName}</Label>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Label className="text-small font-light">
            {companyDetails?.physicalAddress?.city}, {companyDetails?.physicalAddress?.state}
          </Label>
        </div>
      </Card>

      <Button className="w-full mt-4" onClick={onSubmit} loading={confirmingCompany}>
        {confirmingCompany ? 'Hang tight...' : 'Yes'}
      </Button>
    </SetupWorkspaceLayout>
  )
}
